/**
 * Send message via backend proxy
 */
export async function sendMessage(seminarId, teamId, period, mobile, content, lang, clientId, onChunk, page) {
    const controller = new AbortController();
    // The service has a 65-second deadline; allow its response to arrive first.
    const deadline = setTimeout(() => controller.abort(), 70000);
    try {
        const response = await fetch('/api-proxy/service/ai/send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: onChunk ? 'text/event-stream' : 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({ seminarId, teamId, period, mobile, content, lang: lang || 'en', clientId, page })
        });

        if (!response.ok) {
            throw new Error(`Send failed: ${response.status}`);
        }

        if (response.headers.get('Content-Type')?.includes('text/event-stream')) {
            return await readAnswerStream(response.body, onChunk);
        }
        return await response.json();
    } finally {
        clearTimeout(deadline);
    }
}

async function readAnswerStream(body, onChunk) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let streamEnded = false;
    try {
        while (!streamEnded) {
            const { value, done } = await reader.read();
            streamEnded = done;
            buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
            let boundary;
            while ((boundary = /\r?\n\r?\n/.exec(buffer))) {
                const frame = buffer.slice(0, boundary.index);
                buffer = buffer.slice(boundary.index + boundary[0].length);
                let event = '', data = [];
                for (const line of frame.split(/\r?\n/)) {
                    if (line.startsWith('event:')) event = line.slice(6).trim();
                    if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
                }
                if (!data.length) continue;
                const payload = JSON.parse(data.join('\n'));
                if (event === 'chunk' && onChunk) onChunk(payload);
                if (event === 'error') throw new Error(payload.message || 'AI stream failed');
                if (event === 'done') return payload;
            }
        }
        throw new Error('AI stream ended before completion');
    } finally {
        await reader.cancel().catch(() => {});
        reader.releaseLock();
    }
}

/**
 * Load history from backend proxy
 */
export async function loadHistory(seminarId, teamId, period) {
    const params = new URLSearchParams({ seminarId, teamId, period });
    const response = await fetch('/api-proxy/service/ai/history?' + params, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
        throw new Error(`History load failed: ${response.status}`);
    }

    return response.json();
}

/**
 * Submit feedback via backend proxy
 */
export async function submitFeedback(messageId, rating, user) {
    const response = await fetch('/api-proxy/service/ai/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId, rating, user })
    });

    if (!response.ok) {
        throw new Error(`Feedback failed: ${response.status}`);
    }

    return response.json();
}

/**
 * Submit answer to a Socratic trigger question
 */
export async function submitTriggerAnswer(seminarId, teamId, period, questionId, mobile, answer) {
    const response = await fetch('/api-proxy/service/ai/trigger-answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seminarId, teamId, period, questionId, mobile, answer })
    });

    if (!response.ok) {
        throw new Error(`Trigger answer failed: ${response.status}`);
    }

    return response.json();
}

/**
 * Facilitator: load socratic Q&A (AI questions + student answers) for a seminar,
 * grouped by team and period.
 */
export async function loadSocraticBySeminar(seminarId) {
    const params = new URLSearchParams({ seminarId });
    const response = await fetch('/api-proxy/service/ai/socratic?' + params, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
        throw new Error(`Socratic load failed: ${response.status}`);
    }

    return response.json();
}

export async function loadAiHistoryBySeminar(seminarId) {
    const response = await fetch('/api-proxy/service/ai/history-by-seminar?' + new URLSearchParams({ seminarId }), {
        method: 'GET', headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error(`Course AI history load failed: ${response.status}`);
    return response.json();
}

/** Ask a decision question; the existing team socket delivers the popup. */
export async function triggerDecisionQuestion(payload) {
    const response = await fetch('/api-proxy/service/ai/trigger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`AI trigger failed: ${response.status}`);
    return response.json();
}
